import React, {useEffect, useId, useState} from 'react';
import {CircularProgress} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import InputText from "../common/InputText";
import Button from "../common/Button";
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import {getIdMeBaseApiUrl} from "../../utils/UrlConstants";
import Dropdown from "../../utils/components/Dropdown";
import {useMsal} from '@azure/msal-react';
import {toast} from 'react-toastify';
import { displayPhoneNumber } from "../../utils/phoneUtils";
import { useIdentityAppService } from '../../services/IdentityServices';

interface RequestHistoryResponse {
    success: boolean;
    message: string;
    requests: UserAppRequestDto[];
    totalCount: number;
    timestamp: string;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
}

// Types for API responses
interface UserDto {
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
    userStatus: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    organizationName?: string;
    organizationType?: string;
    stateIssuedId?: string;
}

interface UserSearchResponse {
    success: boolean;
    message: string;
    users: UserDto[];
    totalCount: number;
    timestamp: string;
}

interface ErrorResponse {
    success: boolean;
    error: string;
    details: string;
    statusCode: number;
    timestamp: string;
}

// Interface for search criteria
interface SearchCriteria {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    applicationCode?: string;
}

// Filter criteria types
type FilterCriteria1 = 'firstName' | 'lastName' | 'fullName' | 'email' | 'username' | 'applicationCode' | '';
type FilterCriteria2 = 'firstName' | 'lastName' | 'fullName' | 'email' | 'username' | '';

// Interface for initial search state props
interface SearchState {
    selectedOption1: string;
    selectedOption2: string;
    filter1Text: string;
    filter2Text: string;
    hasSearched: boolean;
    displayMode: 'requests' | 'search';
    searchResults: UserDto[];
}

interface ExternalUserManagementProps {
    initialSearchState?: SearchState;
}

const ExternalUserManagement: React.FC<ExternalUserManagementProps> = ({ initialSearchState }) => {
    const navigate = useNavigate();
    const {accounts} = useMsal();

    // Search form state
    const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        applicationCode: ''
    });

    // Filter dropdown state - initialize from props if available
    const [selectedOption1, setSelectedOption1] = useState<FilterCriteria1>(
        (initialSearchState?.selectedOption1 as FilterCriteria1) || ''
    );
    const [selectedOption2, setSelectedOption2] = useState<FilterCriteria2>(
        (initialSearchState?.selectedOption2 as FilterCriteria2) || ''
    );
    const [filter1Text, setFilter1Text] = useState(initialSearchState?.filter1Text || '');
    const [filter2Text, setFilter2Text] = useState(initialSearchState?.filter2Text || '');

    // Row selection state - Changed to support multiple selection
    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    // Filter state for tracking visible rows
    const [currentFilterValue, setCurrentFilterValue] = useState<any[]>([]);

    // User type validation state
    const [showUserTypePopup, setShowUserTypePopup] = useState(false);
    const [pendingUserId, setPendingUserId] = useState<number | null>(null);
    const [selectedUserType, setSelectedUserType] = useState<string | null>(null);

    // Error state
    const [errors, setErrors] = useState({
        fullNameValidation: '',
        phoneValidation: ''
    });

    // Search results state - restore from props if available
    const [searchResults, setSearchResults] = useState<UserDto[]>(initialSearchState?.searchResults || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(initialSearchState?.hasSearched || false);
    const [noUsersFound, setNoUsersFound] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(initialSearchState?.searchResults ? true : false);
    const [displayMode, setDisplayMode] = useState<'requests' | 'search'>(initialSearchState?.displayMode || 'requests');

    // Dropdown choices
    const searchDropDownChoices1 = [
        {label: 'First Name', value: 'firstName'},
        {label: 'Last Name', value: 'lastName'},
        {label: 'Full Name', value: 'fullName'},
        {label: 'Email Address', value: 'email'},
        {label: 'Username', value: 'username'},
        {label: 'Application Code', value: 'applicationCode'}
    ];

    const allSearchDropdownChoices = [
        {label: 'First Name', value: 'firstName'},
        {label: 'Last Name', value: 'lastName'},
        {label: 'Full Name', value: 'fullName'},
        {label: 'Email Address', value: 'email'},
        {label: 'Username', value: 'username'}
    ];

    // Function to load users with any type of requests (Pending, Requested, Approved, Rejected, Revoked, etc.)
    const loadUsersWithRequests = async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            setNoUsersFound(false);
            setHasSearched(false); // Reset search state
            setCurrentFilterValue([]); // Clear grid filter state
            setDisplayMode('requests');

            // Get current user data from sessionStorage
            const currentUserDataStr = sessionStorage.getItem('currentUserData');
            if (!currentUserDataStr) {
                console.error('No current user data found in sessionStorage');
                setError('User session not found. Please log in again.');
                setSearchResults([]);
                setLoading(false);
                setInitialLoadComplete(true);
                return;
            }

            let currentUserData;
            try {
                currentUserData = JSON.parse(currentUserDataStr);
            } catch (parseError) {
                console.error('Error parsing current user data:', parseError);
                setError('Invalid user session data. Please log in again.');
                setSearchResults([]);
                setLoading(false);
                setInitialLoadComplete(true);
                return;
            }

            const url = getIdMeBaseApiUrl() + '/user/with-requests';

            const response = await useIdentityAppService().postUserWithRequests(JSON.stringify({
                    user: currentUserData,
                    userType: 'External'
                }));

            if (response.ok) {
                const data: UserSearchResponse = await response.json();
                setSearchResults(data.users);
                setNoUsersFound(data.users.length === 0);
                console.log(`Loaded ${data.users.length} users with requests`);
            } else {
                const errorData: ErrorResponse = await response.json();
                setError(errorData.error || 'Failed to load users with requests');
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Error loading users with requests:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred while loading users');
            setSearchResults([]);
        } finally {
            setLoading(false);
            setInitialLoadComplete(true);
        }
    };

    // Load users with any type of requests when component mounts
    useEffect(() => {
        loadUsersWithRequests();
    }, []);

    // Debug: Monitor searchResults changes
    useEffect(() => {
        console.log('searchResults state changed:', searchResults);
        console.log('searchResults length:', searchResults.length);
        console.log('displayMode:', displayMode);
        console.log('hasSearched:', hasSearched);
        console.log('loading:', loading);
    }, [searchResults, displayMode, hasSearched, loading]);

    // Function to validate full name format (FirstName LastName)
    const validateFullName = (fullName: string): boolean => {
        const trimmedName = fullName.trim();
        if (!trimmedName) return false;

        const nameParts = trimmedName.split(/\s+/);
        return nameParts.length >= 2 && nameParts.every(part => part.length > 0);
    };

    // Function to check if search is valid
    const isSearchValid = (): boolean => {
        const hasValidFilter1 = selectedOption1 && filter1Text.trim() !== '';
        const hasValidFilter2 = selectedOption2 && filter2Text.trim() !== '';

        // Check if at least one filter is selected and filled
        if (!(hasValidFilter1 || hasValidFilter2)) {
            return false;
        }

        // If Full Name is selected, validate the format
        if (selectedOption1 === 'fullName' && filter1Text.trim()) {
            if (!validateFullName(filter1Text)) {
                return false;
            }
        }

        // Check for any existing errors
        if (errors.fullNameValidation) {
            return false;
        }

        return true;
    };

    // Function to check if at least one row is selected
    const hasSelectedRows = (): boolean => {
        return selectedRows.length > 0;
    };

    // Function to check if exactly one row is selected
    const hasOneSelectedRow = (): boolean => {
        return selectedRows.length === 1;
    };

    // Function to get user type for a given user ID
    const getUserType = (userId: number): string | undefined => {
        const user = searchResults.find(user => user.userId === userId);
        return user?.userType;
    };

    // Function to compute filtered/visible rows based on current grid filter
    const getFilteredResults = (): UserDto[] => {
        if (!currentFilterValue || currentFilterValue.length === 0) {
            return searchResults;
        }

        return searchResults.filter(user => {
            return currentFilterValue.every(filter => {
                if (!filter.value || filter.value === '') {
                    return true; // No filter applied for this column
                }

                const fieldValue = String((user as any)[filter.name] || '').toLowerCase();
                const filterValue = String(filter.value).toLowerCase();

                switch (filter.operator) {
                    case 'contains':
                        return fieldValue.includes(filterValue);
                    case 'startsWith':
                        return fieldValue.startsWith(filterValue);
                    case 'endsWith':
                        return fieldValue.endsWith(filterValue);
                    case 'eq':
                        return fieldValue === filterValue;
                    case 'neq':
                        return fieldValue !== filterValue;
                    default:
                        return fieldValue.includes(filterValue);
                }
            });
        });
    };

    // Function to validate user type compatibility
    const validateUserTypeSelection = (newUserId: number): boolean => {
        if (selectedRows.length === 0) {
            return true; // First selection is always valid
        }

        const newUserType = getUserType(newUserId);
        if (!newUserType) return false;

        // If we already have a selected user type, check if the new one matches
        if (selectedUserType && newUserType !== selectedUserType) {
            return false;
        }

        // Check if all currently selected users have the same type as the new one
        const selectedUserTypes = selectedRows.map(userId => getUserType(userId));
        return selectedUserTypes.every(type => type === newUserType);
    };

    // Function to handle individual checkbox change with user type validation
    const handleCheckboxChange = (userId: number) => {
        const isCurrentlySelected = selectedRows.includes(userId);

        if (isCurrentlySelected) {
            // Removing selection - always allowed
            const newSelectedRows = selectedRows.filter(id => id !== userId);
            setSelectedRows(newSelectedRows);

            // Update selected user type if no users are selected
            if (newSelectedRows.length === 0) {
                setSelectedUserType(null);
            }
        } else {
            // Adding selection - validate user type compatibility
            if (validateUserTypeSelection(userId)) {
                // Compatible user type, add to selection
                const newUserType = getUserType(userId);
                setSelectedRows(prevSelected => [...prevSelected, userId]);
                if (!selectedUserType && newUserType) {
                    setSelectedUserType(newUserType);
                }
            } else {
                // Incompatible user type, show popup
                setPendingUserId(userId);
                setShowUserTypePopup(true);
            }
        }
    };

    // Function to handle popup confirmation (Yes button)
    const handlePopupConfirm = () => {
        if (pendingUserId !== null) {
            const newUserType = getUserType(pendingUserId);
            // Clear previous selections and select only the new user
            setSelectedRows([pendingUserId]);
            setSelectedUserType(newUserType || null);
        }

        // Close popup and reset pending state
        setShowUserTypePopup(false);
        setPendingUserId(null);
    };

    // Function to handle popup cancellation (No button)
    const handlePopupCancel = () => {
        // Close popup and reset pending state without making changes
        setShowUserTypePopup(false);
        setPendingUserId(null);
    };

    // Function to handle "Select All" checkbox - works with filtered rows
    const handleSelectAllChange = (checked: boolean) => {
        const filteredResults = getFilteredResults();

        if (checked) {
            // For select all, we'll only select visible/filtered users of the same type
            if (filteredResults.length > 0) {
                // If we already have a selected user type, use that; otherwise use the first visible user's type
                const targetUserType = selectedUserType || filteredResults[0].userType;
                const sameTypeUsers = filteredResults
                    .filter(user => user.userType === targetUserType)
                    .map(user => user.userId);
                setSelectedRows(sameTypeUsers);
                setSelectedUserType(targetUserType);
            }
        } else {
            // Deselect only the visible/filtered rows
            const filteredUserIds = filteredResults.map(user => user.userId);
            const remainingSelected = selectedRows.filter(id => !filteredUserIds.includes(id));
            setSelectedRows(remainingSelected);

            // Clear user type if no selections remain
            if (remainingSelected.length === 0) {
                setSelectedUserType(null);
            }
        }
    };

    // Function to check if all visible/filtered rows are selected
    const areAllRowsSelected = (): boolean => {
        const filteredResults = getFilteredResults();
        if (filteredResults.length === 0) return false;

        // If no user type is selected yet, check if all visible rows are selected
        if (!selectedUserType) {
            return filteredResults.length > 0 && filteredResults.every(user => selectedRows.includes(user.userId));
        }

        // Only check visible users of the same type as currently selected
        const sameTypeFilteredUsers = filteredResults.filter(user => user.userType === selectedUserType);
        return sameTypeFilteredUsers.length > 0 && sameTypeFilteredUsers.every(user => selectedRows.includes(user.userId));
    };

    // Function to check if some (but not all) visible rows are selected
    const areSomeRowsSelected = (): boolean => {
        const filteredResults = getFilteredResults();
        const filteredUserIds = filteredResults.map(user => user.userId);
        const selectedInFiltered = selectedRows.filter(id => filteredUserIds.includes(id));
        return selectedInFiltered.length > 0 && !areAllRowsSelected();
    };

    // Handler for grid filter value changes
    const handleGridFilterChange = (filterValue: any) => {
        setCurrentFilterValue(filterValue || []);
    };

    // Function to get available choices for Filter Criteria 2 (excluding Filter Criteria 1 selection)
    const getSearchDropdownChoices2 = () => {
        return allSearchDropdownChoices.filter(choice => choice.value !== selectedOption1);
    };

    // Function to handle filter 1 text change
    const handleFilter1TextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Filter 1 text changed:', e.target.value); // Debug log
        const value = e.target.value;
        setFilter1Text(value);

        // Validate full name format if Full Name is selected
        if (selectedOption1 === 'fullName') {
            if (value.trim() && !validateFullName(value)) {
                setErrors(prev => ({
                    ...prev,
                    fullNameValidation: 'Full name should be in format: FirstName LastName'
                }));
            } else {
                setErrors(prev => ({ ...prev, fullNameValidation: '' }));
            }
        }
    };

    // Updated filter 1 dropdown change handler
    const handleFilter1DropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        console.log('Filter 1 dropdown changed:', e.target.value);
        const value = e.target.value as FilterCriteria1;
        setSelectedOption1(value);
        setFilter1Text('');
        setErrors(prev => ({...prev, fullNameValidation: ''}));

        // Clear Filter 2 if it has the same value as Filter 1
        if (selectedOption2 === value) {
            setSelectedOption2('');
            setFilter2Text('');
        }
    };

    // Updated filter 2 dropdown change handler
    const handleFilter2DropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        console.log('Filter 2 dropdown changed:', e.target.value);
        const value = e.target.value as FilterCriteria2;
        setSelectedOption2(value);
        setFilter2Text('');
    };

    // Updated filter 2 text change handler to handle email validation
    const handleFilter2TextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Filter 2 text changed:', e.target.value);
        const value = e.target.value;
        setFilter2Text(value);

        // Add email validation if needed for Filter 2
        if (selectedOption2 === 'fullName') {
            if (value.trim() && !validateFullName(value)) {
                setErrors(prev => ({
                    ...prev,
                    fullNameValidation: 'Full name should be in format: FirstName LastName'
                }));
            } else {
                setErrors(prev => ({...prev, fullNameValidation: ''}));
            }
        }
    };

    // Updated buildSearchCriteria function to handle both filters
    const buildSearchCriteria = (): SearchCriteria => {
        const criteria: SearchCriteria = {
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            applicationCode: ''
        };

        // Handle Filter 1
        if (selectedOption1 && filter1Text.trim()) {
            switch (selectedOption1) {
                case 'firstName':
                    criteria.firstName = filter1Text.trim();
                    break;
                case 'lastName':
                    criteria.lastName = filter1Text.trim();
                    break;
                case 'fullName':
                    if (validateFullName(filter1Text)) {
                        const nameParts = filter1Text.trim().split(/\s+/);
                        criteria.firstName = nameParts[0];
                        criteria.lastName = nameParts.slice(1).join(' ');
                    }
                    break;
                case 'email':
                    criteria.email = filter1Text.trim();
                    break;
                case 'username':
                    criteria.username = filter1Text.trim();
                    break;
                case 'applicationCode':
                    criteria.applicationCode = filter1Text.trim();
                    break;
            }
        }

        // Handle Filter 2
        if (selectedOption2 && filter2Text.trim()) {
            switch (selectedOption2) {
                case 'firstName':
                    // Only set if not already set by Filter 1
                    if (!criteria.firstName) {
                        criteria.firstName = filter2Text.trim();
                    }
                    break;
                case 'lastName':
                    // Only set if not already set by Filter 1
                    if (!criteria.lastName) {
                        criteria.lastName = filter2Text.trim();
                    }
                    break;
                case 'fullName':
                    if (validateFullName(filter2Text)) {
                        const nameParts = filter2Text.trim().split(/\s+/);
                        // Only set if not already set by Filter 1
                        if (!criteria.firstName) {
                            criteria.firstName = nameParts[0];
                        }
                        if (!criteria.lastName) {
                            criteria.lastName = nameParts.slice(1).join(' ');
                        }
                    }
                    break;
                case 'email':
                    // Only set if not already set by Filter 1
                    if (!criteria.email) {
                        criteria.email = filter2Text.trim();
                    }
                    break;
                case 'username':
                    // Only set if not already set by Filter 1
                    if (!criteria.username) {
                        criteria.username = filter2Text.trim();
                    }
                    break;
            }
        }

        return criteria;
    };

    // Updated placeholder functions
    const getFilter1Placeholder = (): string => {
        switch (selectedOption1) {
            case 'firstName':
                return 'Enter first name';
            case 'lastName':
                return 'Enter last name';
            case 'fullName':
                return 'Enter full name (FirstName LastName)';
            case 'email':
                return 'Enter email address';
            case 'username':
                return 'Enter username';
            case 'applicationCode':
                return 'Enter application code';
            default:
                return 'Select filter criteria first';
        }
    };

    const getFilter2Placeholder = (): string => {
        switch (selectedOption2) {
            case 'firstName':
                return 'Enter first name';
            case 'lastName':
                return 'Enter last name';
            case 'fullName':
                return 'Enter full name (FirstName LastName)';
            case 'email':
                return 'Enter email address';
            case 'username':
                return 'Enter username';
            default:
                return 'Select filter criteria first';
        }
    };

    // Function to search users via Microsoft Graph API through backend
    const searchUsersViaGraphAPI = async (searchCriteria: SearchCriteria): Promise<UserDto[]> => {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (searchCriteria.firstName && searchCriteria.firstName.trim()) {
                params.append('firstName', searchCriteria.firstName.trim());
            }
            if (searchCriteria.lastName && searchCriteria.lastName.trim()) {
                params.append('lastName', searchCriteria.lastName.trim());
            }
            if (searchCriteria.email && searchCriteria.email.trim()) {
                params.append('email', searchCriteria.email.trim());
            }

            const queryString = params.toString();

            const response = await useIdentityAppService().fetchUserSearchGraph(queryString ? `?${queryString}` : '');

            if (response.ok) {
                const data: UserSearchResponse = await response.json();
                return data.users;
            } else {
                console.error('Failed to search users via Graph API:', await response.text());
                throw new Error('Failed to search users via Microsoft Graph API');
            }
        } catch (err) {
            console.error('Error searching users via Graph API:', err);
            throw err;
        }
    };

    // Function to clear search form and reload users with requests
    const handleClear = () => {
        console.log('Clear button clicked'); // Debug log
        setSelectedOption1('');
        setSelectedOption2('');
        setFilter1Text('');
        setFilter2Text('');
        setSearchCriteria({
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            applicationCode: ''
        });
        setSelectedRows([]); // Clear selections
        setSelectedUserType(null); // Clear user type
        setCurrentFilterValue([]); // Clear grid filter state
        setErrors({
            fullNameValidation: '',
            phoneValidation: ''
        });
        setHasSearched(false);
        setError(null);

        // Reload users with requests
        loadUsersWithRequests();
    };

    // Function to call the search API
    const searchUsers = async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);
            setNoUsersFound(false);
            setSelectedRows([]); // Clear selections on new search
            setSelectedUserType(null); // Clear user type on new search
            setCurrentFilterValue([]); // Clear grid filter state on new search
            setDisplayMode('search');

            const searchCriteria = buildSearchCriteria();

            // Check if searching by application code
            if (searchCriteria.applicationCode && searchCriteria.applicationCode.trim()) {
                // Search through USER_APP_REQUEST by application code
                await searchUsersByApplicationCode(searchCriteria.applicationCode.trim());
            } else {
                // Regular user search
                // Build query parameters
                const params = new URLSearchParams();
                if (searchCriteria.firstName.trim()) {
                    params.append('firstName', searchCriteria.firstName.trim());
                }
                if (searchCriteria.lastName.trim()) {
                    params.append('lastName', searchCriteria.lastName.trim());
                }
                if (searchCriteria.email.trim()) {
                    const encodedEmail = btoa(searchCriteria.email.trim());
                    params.append('email', encodedEmail);
                }
                if (searchCriteria.username.trim()) {
                    params.append('username', searchCriteria.username.trim());
                }
                // Filter for External users only (Citizen, Member, Trading Partner)
                params.append('userType', 'External');

                const queryString = params.toString();

                const response = await useIdentityAppService().getUserSearchByQuery(queryString ? `?${queryString}` : '');

                if (response.ok) {
                    // Success response (200 OK)
                    const data = await response.json();
                    console.log('Search API response:', data);
                    console.log('Users from API:', data.users);
                    console.log('Number of users:', data.users?.length);

                    // Handle multiple response formats:
                    // 1. Array directly: [{user1}, {user2}, ...]
                    // 2. Object with users property: {users: [...], ...}
                    // 3. Object with data property: {data: [...], ...}
                    let rawUsers: any[] = [];
                    if (Array.isArray(data)) {
                        rawUsers = data;
                    } else if (Array.isArray(data.users)) {
                        rawUsers = data.users;
                    } else if (Array.isArray(data.data)) {
                        rawUsers = data.data;
                    }

                    // Log first user to see actual field names
                    if (rawUsers.length > 0) {
                        console.log('First user object keys:', Object.keys(rawUsers[0]));
                        console.log('First user object:', rawUsers[0]);
                    }

                    // Map API field names to expected field names
                    const users: UserDto[] = rawUsers.map((user: any) => ({
                        userId: user.userId ?? user.user_id ?? user.id ?? 0,
                        userLogin: user.userLogin ?? user.user_login ?? user.username ?? user.login ?? '',
                        userType: user.userType ?? user.user_type ?? user.type ?? '',
                        userEmail: user.userEmail ?? user.user_email ?? user.email ?? '',
                        userPhone: user.userPhone ?? user.user_phone ?? user.phone ?? user.phoneNumber ?? '',
                        userIdpGuid: user.userIdpGuid ?? user.user_idp_guid ?? user.idpGuid ?? '',
                        firstName: user.firstName ?? user.first_name ?? user.firstname ?? '',
                        lastName: user.lastName ?? user.last_name ?? user.lastname ?? '',
                        createdDate: user.createdDate ?? user.created_date ?? user.createdAt ?? '',
                        modifiedDate: user.modifiedDate ?? user.modified_date ?? user.updatedAt ?? '',
                        notes: user.notes ?? '',
                        userStatus: user.userStatus ?? user.user_status ?? user.status ?? '',
                        addressLine1: user.addressLine1 ?? user.address_line1 ?? user.address1 ?? '',
                        addressLine2: user.addressLine2 ?? user.address_line2 ?? user.address2 ?? '',
                        city: user.city ?? '',
                        state: user.state ?? '',
                        zip: user.zip ?? user.zipCode ?? user.postalCode ?? '',
                        organizationName: user.organizationName ?? user.organization_name ?? user.orgName ?? '',
                        organizationType: user.organizationType ?? user.organization_type ?? user.orgType ?? '',
                        stateIssuedId: user.stateIssuedId ?? user.state_issued_id ?? user.stateId ?? ''
                    }));

                    console.log('Mapped users:', users);
                    console.log('Setting searchResults to:', users);
                    setSearchResults(users);
                    setHasSearched(true);
                    setNoUsersFound(users.length === 0);
                } else if (response.status === 404) {
                    // No users found in primary API, fallback to Microsoft Graph API
                    console.log('Primary API returned 404, trying Microsoft Graph API fallback...');
                    try {
                        const graphUsers = await searchUsersViaGraphAPI(searchCriteria);
                        console.log('Graph API returned users:', graphUsers);
                        const users = Array.isArray(graphUsers) ? graphUsers : [];
                        setSearchResults(users);
                        setHasSearched(true);
                        setNoUsersFound(users.length === 0);
                        if (users.length > 0) {
                            toast.success('Users found via Microsoft Graph API');
                        }
                    } catch (graphError) {
                        console.error('Graph API fallback failed:', graphError);
                        setSearchResults([]);
                        setHasSearched(true);
                        setNoUsersFound(true);
                    }
                } else if (response.status === 400) {
                    // Bad request (400 BAD REQUEST)
                    const errorData: ErrorResponse = await response.json();
                    setError(errorData.details || errorData.error);
                    setHasSearched(false);
                    setNoUsersFound(false);
                } else {
                    // Other errors - try Graph API fallback
                    console.log('Primary API failed, trying Microsoft Graph API fallback...');
                    try {
                        const graphUsers = await searchUsersViaGraphAPI(searchCriteria);
                        console.log('Graph API returned users:', graphUsers);
                        const users = Array.isArray(graphUsers) ? graphUsers : [];
                        setSearchResults(users);
                        setHasSearched(true);
                        setNoUsersFound(users.length === 0);
                        if (users.length > 0) {
                            toast.success('Users found via Microsoft Graph API');
                        }
                    } catch (graphError) {
                        const errorData: ErrorResponse = await response.json();
                        setError(errorData.error || 'An error occurred while searching for users');
                        setHasSearched(false);
                        setNoUsersFound(false);
                    }
                }
            }
        } catch (err) {
            console.error('Error searching users:', err);
            // Try Graph API fallback on network error
            console.log('Network error occurred, trying Microsoft Graph API fallback...');
            try {
                const searchCriteria = buildSearchCriteria();
                const graphUsers = await searchUsersViaGraphAPI(searchCriteria);
                console.log('Graph API returned users:', graphUsers);
                const users = Array.isArray(graphUsers) ? graphUsers : [];
                setSearchResults(users);
                setHasSearched(true);
                setNoUsersFound(users.length === 0);
                if (users.length > 0) {
                    toast.success('Users found via Microsoft Graph API');
                }
            } catch (graphError) {
                console.error('Graph API fallback failed:', graphError);
                setError(err instanceof Error ? err.message : 'Network error occurred');
                setHasSearched(false);
                setNoUsersFound(false);
            }
        } finally {
            setLoading(false);
        }
    };

    // Function to search users by application code through USER_APP_REQUEST
    // Only returns users if the logged-in user is admin to the app
    const searchUsersByApplicationCode = async (applicationCode: string): Promise<void> => {
        try {
            if (!accounts[0]?.name) {
                setError('Unable to determine user identity for application search');
                return;
            }

            const adminUserId = accounts[0].idTokenClaims?.oid || accounts[0].name;

            const response = await useIdentityAppService().searchUsersByApplicationCode(applicationCode, adminUserId);

            if (response.ok) {
                // Success response (200 OK)
                const data = await response.json();
                console.log('Search by application code response:', data);
                // Handle multiple response formats
                let rawUsers: any[] = [];
                if (Array.isArray(data)) {
                    rawUsers = data;
                } else if (Array.isArray(data.users)) {
                    rawUsers = data.users;
                } else if (Array.isArray(data.data)) {
                    rawUsers = data.data;
                }

                // Map API field names to expected field names
                const users: UserDto[] = rawUsers.map((user: any) => ({
                    userId: user.userId ?? user.user_id ?? user.id ?? 0,
                    userLogin: user.userLogin ?? user.user_login ?? user.username ?? user.login ?? '',
                    userType: user.userType ?? user.user_type ?? user.type ?? '',
                    userEmail: user.userEmail ?? user.user_email ?? user.email ?? '',
                    userPhone: user.userPhone ?? user.user_phone ?? user.phone ?? user.phoneNumber ?? '',
                    userIdpGuid: user.userIdpGuid ?? user.user_idp_guid ?? user.idpGuid ?? '',
                    firstName: user.firstName ?? user.first_name ?? user.firstname ?? '',
                    lastName: user.lastName ?? user.last_name ?? user.lastname ?? '',
                    createdDate: user.createdDate ?? user.created_date ?? user.createdAt ?? '',
                    modifiedDate: user.modifiedDate ?? user.modified_date ?? user.updatedAt ?? '',
                    notes: user.notes ?? '',
                    userStatus: user.userStatus ?? user.user_status ?? user.status ?? '',
                    addressLine1: user.addressLine1 ?? user.address_line1 ?? user.address1 ?? '',
                    addressLine2: user.addressLine2 ?? user.address_line2 ?? user.address2 ?? '',
                    city: user.city ?? '',
                    state: user.state ?? '',
                    zip: user.zip ?? user.zipCode ?? user.postalCode ?? '',
                    organizationName: user.organizationName ?? user.organization_name ?? user.orgName ?? '',
                    organizationType: user.organizationType ?? user.organization_type ?? user.orgType ?? '',
                    stateIssuedId: user.stateIssuedId ?? user.state_issued_id ?? user.stateId ?? ''
                }));

                setSearchResults(users);
                setHasSearched(true);
                setNoUsersFound(users.length === 0);
            } else if (response.status === 404) {
                // No users found (404 NOT FOUND)
                const data: UserSearchResponse = await response.json();
                setSearchResults([]);
                setHasSearched(true);
                setNoUsersFound(true);
            } else if (response.status === 403) {
                // Forbidden - user is not admin to this app
                setError('You do not have admin access to this application');
                setSearchResults([]);
                setHasSearched(false);
                setNoUsersFound(false);
            } else if (response.status === 400) {
                // Bad request (400 BAD REQUEST)
                const errorData: ErrorResponse = await response.json();
                setError(errorData.details || errorData.error);
                setHasSearched(false);
                setNoUsersFound(false);
            } else {
                // Other errors
                const errorData: ErrorResponse = await response.json();
                setError(errorData.error || 'An error occurred while searching for users by application');
                setHasSearched(false);
                setNoUsersFound(false);
            }
        } catch (err) {
            console.error('Error searching users by application code:', err);
            setError(err instanceof Error ? err.message : 'Network error occurred');
            setHasSearched(false);
            setNoUsersFound(false);
        }
    };

    // Function to handle search button click
    const handleSearch = () => {
        console.log('Search button clicked'); // Debug log
        if (isSearchValid()) {
            searchUsers();
        }
    };

    // Function to handle "Create User" button click
    const handleCreateUser = () => {
        console.log('Create User button clicked'); // Debug log
        const searchCriteria = buildSearchCriteria();

        // Navigate to self-registration page with pre-filled data if available
        const params = new URLSearchParams();
        if (searchCriteria.firstName) params.append('firstName', searchCriteria.firstName);
        if (searchCriteria.lastName) params.append('lastName', searchCriteria.lastName);
        if (searchCriteria.email) params.append('email', searchCriteria.email);

        const queryString = params.toString();
        navigate(`/self-registration${queryString ? `?${queryString}` : ''}`, { state: { from: 'admin-user-management' } });
    };

    // Function to handle "Assign Roles & Permissions" button click
    const handleAssignRoles = () => {
        console.log('Assign Roles clicked for users:', selectedRows);
        // Get selected user details
        const selectedUsers = searchResults.filter(user => selectedRows.includes(user.userId));
        console.log('Selected users details:', selectedUsers);
        console.log('Selected user type:', selectedUserType);

        // Navigate to role assignment page with selected users data
        navigate('/assign-roles', {
            state: {
                selectedUsers: selectedUsers,
                userType: 'External',
                searchState: {
                    selectedOption1,
                    selectedOption2,
                    filter1Text,
                    filter2Text,
                    hasSearched,
                    displayMode,
                    searchResults
                }
            }
        });
    };

    // Function to handle "Manage User" button click
    const handleManageUser = () => {
        if (selectedRows.length === 1) {
            const selectedUser = searchResults.find(user => user.userId === selectedRows[0]);
            if (selectedUser) {
                console.log('Managing user:', selectedUser);
                // Navigate to user management page with selected user data
                navigate('/manage-user', {
                    state: {
                        user: selectedUser,
                        userTypeSelection: 'External',
                        searchState: {
                            selectedOption1,
                            selectedOption2,
                            filter1Text,
                            filter2Text,
                            hasSearched,
                            displayMode,
                            searchResults
                        }
                    }
                });
            }
        } else {
            toast.error('Please select exactly one user to manage');
        }
    };

    // Function to toggle between showing all users and users with requests
    // Define grid columns for search results with proper multi-selection
    const userGridColumns = [
        {
            name: 'select',
            header: () => (
                <input
                    type="checkbox"
                    checked={areAllRowsSelected()}
                    ref={(input) => {
                        if (input) input.indeterminate = areSomeRowsSelected();
                    }}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                    style={{cursor: 'pointer'}}
                />
            ),
            defaultWidth: 60,
            render: ({data}: any) => (
                <input
                    type="checkbox"
                    checked={selectedRows.includes(data.userId)}
                    onChange={() => handleCheckboxChange(data.userId)}
                    style={{cursor: 'pointer'}}
                />
            ),
            sortable: false,
            filterable: false
        },
        {
            name: 'userLogin',
            header: 'User Name',
            defaultFlex: 1,
            minWidth: 160,
        },
        {
            name: 'firstName',
            header: 'First Name',
            defaultFlex: 1,
            minWidth: 120,
            type: 'string',
        },
        {
            name: 'lastName',
            header: 'Last Name',
            defaultFlex: 1,
            minWidth: 120,
            type: 'string',
        },
        {
            name: 'userEmail',
            header: 'Email Address',
            defaultFlex: 2,
            minWidth: 200,
            type: 'string',
        },
        {
            name: 'userPhone',
            header: 'Phone Number',
            defaultFlex: 1,
            minWidth: 140,
            render: ({ value }: any) => displayPhoneNumber(value) || 'N/A',
        },
        {
            name: 'userType',
            header: 'User Type',
            defaultFlex: 1,
            minWidth: 120,
        },
        {
            name: 'userStatus',
            header: 'User Status',
            defaultFlex: 1,
            minWidth: 120,
            render: ({value}: { value: string | null }) => {
                const displayValue = value || 'Unknown';
                const getStatusStyles = (status: string) => {
                    switch (status?.toUpperCase()) {
                        case 'ACTIVE':
                            return {
                                backgroundColor: '#E8F5E8',
                                color: '#4CAF50'
                            };
                        case 'PENDING':
                        case 'INVITED':
                            return {
                                backgroundColor: '#fff3cd',
                                color: '#856404'
                            };
                        case 'SUSPENDED':
                        case 'DISABLED':
                            return {
                                backgroundColor: '#FFF3E0',
                                color: '#FF9800'
                            };
                        case 'LOCKED':
                            return {
                                backgroundColor: '#FFEBEE',
                                color: '#f44336'
                            };
                        case 'PROVISIONING':
                            return {
                                backgroundColor: '#E3F2FD',
                                color: '#1976D2'
                            };
                        case 'DEPROVISIONING':
                            return {
                                backgroundColor: '#FCE4EC',
                                color: '#C2185B'
                            };
                        case 'TERMINATED':
                        case 'DELETED':
                            return {
                                backgroundColor: '#FFEBEE',
                                color: '#f44336'
                            };
                        case 'EXPIRED':
                            return {
                                backgroundColor: '#F3E5F5',
                                color: '#7B1FA2'
                            };
                        case 'INACTIVE':
                            return {
                                backgroundColor: '#F5F5F5',
                                color: '#9E9E9E'
                            };
                        case 'UNKNOWN':
                            return {
                                backgroundColor: '#f5f5f5',
                                color: '#666'
                            };
                        default:
                            return {
                                backgroundColor: '#f5f5f5',
                                color: '#666'
                            };
                    }
                };

                const statusStyles = getStatusStyles(displayValue);

                return (
                    <span style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}>
                        {displayValue}
                    </span>
                );
            }
        },
        {
            name: 'createdDate',
            header: 'Created Date',
            defaultFlex: 1,
            minWidth: 140,
            render: ({value}: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        }
    ];

    const filterValue = [
        {
            name: 'userEmail',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userLogin',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'firstName',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'lastName',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userType',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userStatus',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'userPhone',
            operator: 'contains',
            type: 'string',
            value: '',
        },
        {
            name: 'createdDate',
            operator: 'contains',
            type: 'string',
            value: '',
        },
    ];

    // Function to get the grid title
    const getGridTitle = (): string => {
        if (loading) {
            return 'Loading...';
        }

        let title = '';

        switch(displayMode) {
            case 'requests':
                title = `Users with Application Access (${searchResults.length} user${searchResults.length !== 1 ? 's' : ''})`;
                break;
            case 'search':
                title = `Search Results (${searchResults.length} user${searchResults.length !== 1 ? 's' : ''} found)`;
                break;
            default:
                title = `Users (${searchResults.length})`;
        }

        if (selectedUserType && selectedRows.length > 0) {
            return `${title} - Selected: ${selectedUserType} (${selectedRows.length})`;
        }
        return title;
    };

    const handleRequestHistory = async () => {
        console.log('Request History button clicked');

        if (!accounts[0]?.name) {
            toast.error('Unable to determine user identity for request history');
            return;
        }

        try {
            setLoading(true);

            console.log(accounts[0]);
            const assignedTo = accounts[0].idTokenClaims?.oid || accounts[0].name;
            const userName = accounts[0].name;
            const url = `${getIdMeBaseApiUrl()}/request-history?assignedTo=${encodeURIComponent(assignedTo)}`;

            const response = await useIdentityAppService().fetchRequestHistoryByAssignedUser(assignedTo);

            if (response.ok) {
                const data: RequestHistoryResponse = await response.json();

                // Navigate to Request History page with the fetched data
                navigate('/request-history', {
                    state: {
                        requests: data.requests,
                        assignedTo: userName,
                        totalCount: data.totalCount,
                        userType: 'External',
                        searchState: {
                            selectedOption1,
                            selectedOption2,
                            filter1Text,
                            filter2Text,
                            hasSearched,
                            displayMode,
                            searchResults
                        }
                    }
                });
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to fetch request history');
            }
        } catch (error) {
            console.error('Error fetching request history:', error);
            toast.error('Network error occurred while fetching request history');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* User Type Validation Popup */}
            {showUserTypePopup && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }}>
                        <h3 style={{
                            marginTop: 0,
                            marginBottom: '20px',
                            color: '#333',
                            textAlign: 'center'
                        }}>
                            User Type Mismatch
                        </h3>
                        <p style={{
                            marginBottom: '25px',
                            lineHeight: '1.5',
                            color: '#555',
                            textAlign: 'center'
                        }}>
                            Only users with matching user types can be selected together for role assignment.
                            Your current selection will be cleared and replaced with this user.
                            Do you want to proceed?
                        </p>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '15px'
                        }}>
                            <Button
                                onClick={handlePopupConfirm}
                            >
                                Yes, Continue
                            </Button>
                            <Button
                                onClick={handlePopupCancel}
                            >
                                No, Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Filters Section with improved styling */}
            <div className={"external-filters"} style={{
                position: 'relative',
                zIndex: 10,
                pointerEvents: 'auto',
                marginTop: '60px'
            }}>
                {/* Filter Criteria 1 */}
                <div className={"external-filters-elem"} style={{
                    maxWidth: '220px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <Dropdown
                        choices={searchDropDownChoices1}
                        emptyChoiceLabel={"Select Filter Criteria 1"}
                        label={""}
                        name={"filterCriteria1"}
                        value={selectedOption1}
                        onChange={handleFilter1DropdownChange}
                    />
                </div>
                <div className="external-filters-elem" style={{
                    maxWidth: '250px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <InputText
                        context="registration"
                        name="filter1"
                        placeholder={getFilter1Placeholder()}
                        value={filter1Text}
                        onChange={handleFilter1TextChange}
                        small={true}
                        readOnly={!selectedOption1}
                    />
                    {errors.fullNameValidation && (
                        <div style={{color: "red", marginTop: "5px"}}>
                            {errors.fullNameValidation}
                        </div>
                    )}
                </div>

                {/* Filter Criteria 2 */}
                <div className="external-filters-elem" style={{
                    maxWidth: '220px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <Dropdown
                        choices={getSearchDropdownChoices2()}
                        emptyChoiceLabel="Select Filter Criteria 2"
                        name="filterCriteria2"
                        label=""
                        value={selectedOption2}
                        onChange={handleFilter2DropdownChange}
                    />
                </div>
                <div className="external-filters-elem" style={{
                    maxWidth: '250px',
                    position: 'relative',
                    zIndex: 11
                }}>
                    <InputText
                        context="registration"
                        name="filter2"
                        placeholder={getFilter2Placeholder()}
                        value={filter2Text}
                        small={true}
                        onChange={handleFilter2TextChange}
                        readOnly={!selectedOption2}
                    />
                </div>

                {/* Action Buttons with improved styling */}
                <div className="external-filters-action external-filters-action-right" style={{
                    position: 'relative',
                    zIndex: 11,
                    pointerEvents: 'auto'
                }}>
                    <Button
                        disabled={!(
                            (selectedOption1 && filter1Text.trim()) ||
                            (selectedOption2 && filter2Text.trim())
                        )}
                        className="OkButton"
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                    <Button
                        disabled={!isSearchValid() || loading}
                        className="OkButton"
                        onClick={handleSearch}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </Button>
                    <Button
                        onClick={handleCreateUser}
                        className="OkButton"
                    >
                        Create Accounts
                    </Button>
                    <Button
                        className="OkButton"
                        disabled={!hasSelectedRows()} // Enable only when rows are selected
                        onClick={handleAssignRoles}
                    >
                        Assign Roles & Permissions
                    </Button>
                    <Button
                        className="OkButton"
                        disabled={!hasOneSelectedRow()} // Enable only when exactly one row is selected
                        onClick={handleManageUser}
                    >
                        Manage User
                    </Button>
                    <Button
                        className="OkButton"
                        onClick={handleRequestHistory}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : 'Request History'}
                    </Button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    marginTop: '20px',
                    padding: '16px',
                    color: '#c62828'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Loading Message */}
            {loading && !initialLoadComplete && (
                <div style={{
                    marginTop: '30px',
                    textAlign: 'center',
                    padding: '20px'
                }}>
                    <CircularProgress size={24} style={{marginRight: '10px'}}/>
                    Loading users...
                </div>
            )}

            {/* Search Results Grid or All Users Grid */}
            {(searchResults.length > 0 || (initialLoadComplete && !loading)) && (
                <div style={{marginTop: '30px'}}>
                    <h4 style={{marginBottom: '20px'}}>
                        {getGridTitle()}
                    </h4>
                    {(() => {
                        console.log('Rendering table section - searchResults.length:', searchResults.length);
                        console.log('searchResults data:', searchResults);
                        return null;
                    })()}
                    {searchResults.length > 0 ? (
                        <ReactDataGrid
                            key={`${displayMode}-${searchResults.length}`}
                            licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                            idProperty="userId"
                            pagination
                            columns={userGridColumns}
                            dataSource={searchResults}
                            sortable={true}
                            defaultLimit={10}
                            rowHeight={50}
                            className="pointer-cursor-row"
                            enableColumnAutosize={true}
                            style={{minHeight: 400}}
                            defaultFilterValue={filterValue}
                            onFilterValueChange={handleGridFilterChange}
                        />
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px',
                            color: '#666',
                            fontSize: '16px'
                        }}>
                            {hasSearched ? 'No users found matching your search criteria.' : 'No users found in the system.'}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ExternalUserManagement;